import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import {
  bordesYichang, listCierresYiMercado, listFichasYiBodega, listFichasYiMercado, listFichasYiModerno,
} from '../../api/yichang';
import { agruparPersonas, claveTexto, STOPWORDS_LUGAR, STOPWORDS_PERSONA } from '../../domain/pg/normalizar';
import { coincideTodasLasPalabras } from '../../domain/tableFilters';
import { presenciaModerno } from '../../domain/yichang/moderno';
import { presenciaBodega, presenciaMercado } from '../../domain/yichang/tradicional';
import {
  NOMBRE_MARCA,
  type CierreYiMercado, type FichaYiBodega, type FichaYiMercado, type FichaYiModerno, type InstrumentoYi,
  type MarcaYi, type Presencia,
} from '../../domain/yichang/tipos';
import RegistrosYichang from './yichang/RegistrosYichang';
import ResumenYichang from './yichang/ResumenYichang';

const TZ = 'America/Lima';
const hoyLima = () => formatInTimeZone(new Date(), TZ, 'yyyy-MM-dd');

const NOMBRE_INSTRUMENTO: Record<InstrumentoYi, string> = {
  moderno: 'canal moderno',
  bodegas: 'bodegas',
  mercados: 'mercados',
};

const NOMBRE_FOCO: Record<MarcaYi, string> = {
  las_dos: "Pringles y Kellogg's",
  pringles: 'Pringles',
  kelloggs: "Kellogg's",
};

/** Agrupa textos escritos a mano por su clave normalizada y los rotula con la grafía más frecuente. */
function agruparTextos(valores: readonly (string | null)[], quitar: readonly string[]) {
  const m = new Map<string, Map<string, number>>();
  for (const v of valores) {
    const k = claveTexto(v, quitar);
    if (k === null) continue;
    const g = m.get(k) ?? new Map<string, number>();
    g.set((v ?? '').trim(), (g.get((v ?? '').trim()) ?? 0) + 1);
    m.set(k, g);
  }
  return [...m.entries()].map(([clave, g]) => {
    const grafias = [...g.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'es'));
    return { clave, rotulo: grafias[0]?.[0] ?? clave, n: grafias.reduce((s, x) => s + x[1], 0), grafias: grafias.length };
  }).sort((a, b) => b.n - a.n || a.rotulo.localeCompare(b.rotulo, 'es'));
}

interface Filtro { sitio: string; persona: string; busqueda: string; soloSenal: boolean }

/**
 * ¿La ficha pasa la segunda barra? `sitioTexto` se compara por clave normalizada (con `quitar` como
 * palabras de relleno: en mercados, «Mercado», «de»…), el supervisor también, y la búsqueda exige
 * TODAS las palabras en cualquiera de los campos del pajar.
 */
function pasaFiltro(
  f: Filtro, quitar: readonly string[], flags: readonly string[], sitioTexto: string | null, sup: string | null,
  ...pajar: (string | null)[]
): boolean {
  if (f.soloSenal && flags.length === 0) return false;
  if (f.sitio !== '' && claveTexto(sitioTexto, quitar) !== f.sitio) return false;
  if (f.persona !== '' && claveTexto(sup, STOPWORDS_PERSONA) !== f.persona) return false;
  return coincideTodasLasPalabras(f.busqueda, sup, ...pajar);
}

/**
 * Levantamiento de información · **Yichang** (Pringles y Kellogg's).
 *
 * La misma estructura que la de P&G, a propósito: quien revisa las dos no tiene que aprender dos
 * pantallas. Primera barra = lo que viaja al servidor; segunda barra = lo que recorta lo ya traído;
 * línea de reconciliación; y dos solapas calculadas sobre **exactamente las mismas filas**.
 *
 * ## El filtro de marca no descarta fichas
 *
 * 🔴 Una visita mide las dos marcas: la misma tienda tiene la góndola de snacks (Pringles) y la de
 * cereales (Kellogg's). Elegir una marca **enfoca** la vista en su parte de cada ficha —la columna
 * «qué se midió», el detalle y el resumen— sin sacar ninguna fila. Recortar filas es otra cosa, y se
 * pide aparte con «Solo donde se encontró», que dice en la línea de reconciliación cuántas quedaron
 * afuera por no tener la marca y cuántas porque **no se puede saber**: esas no se cuentan como
 * ausencia.
 */
export default function LevantamientoYichangBandeja() {
  const [instrumento, setInstrumento] = useState<InstrumentoYi>('moderno');
  const [marca, setMarca] = useState<MarcaYi>('las_dos');
  const [soloPresente, setSoloPresente] = useState(false);
  const [solapa, setSolapa] = useState<'registros' | 'resumen'>('registros');

  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState(hoyLima);
  const [bordes, setBordes] = useState<{ primera: string | null; ultima: string | null; total: number } | null>(null);

  const [fichasMod, setFichasMod] = useState<FichaYiModerno[]>([]);
  const [fichasBod, setFichasBod] = useState<FichaYiBodega[]>([]);
  const [fichasMer, setFichasMer] = useState<FichaYiMercado[]>([]);
  const [cierres, setCierres] = useState<CierreYiMercado[]>([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');

  const [sitio, setSitio] = useState('');
  const [persona, setPersona] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [soloSenal, setSoloSenal] = useState(false);

  /** El «desde» por omisión es la fecha de la PRIMERA ficha del instrumento, no el 1 del mes. */
  useEffect(() => {
    let vivo = true;
    setBordes(null);
    void bordesYichang(instrumento)
      .then((b) => {
        if (!vivo) return;
        setBordes(b);
        setDesde(b.primera ?? hoyLima());
        setHasta(b.ultima !== null && b.ultima > hoyLima() ? b.ultima : hoyLima());
      })
      .catch((e: unknown) => {
        if (vivo) setError(e instanceof Error ? e.message : 'No se pudo leer el rango del estudio.');
      });
    return () => { vivo = false; };
  }, [instrumento]);

  /**
   * Cada carga lleva su número y solo la ÚLTIMA escribe. Al cambiar de instrumento salen dos: una con
   * el rango del instrumento anterior (el efecto corre antes de que lleguen los bordes nuevos) y otra
   * con el rango nuevo. Si la primera tardara más, pisaría a la segunda y la pantalla mostraría las
   * fichas de un rango que ya no es el que dice arriba, sin ningún error.
   */
  const pedido = useRef(0);
  const cargar = useCallback(async () => {
    if (desde === '' || hasta === '') return;
    if (desde > hasta) { setError('El rango de fechas está invertido.'); return; }
    const este = ++pedido.current;
    const vigente = () => este === pedido.current;
    setCargando(true);
    setError('');
    try {
      if (instrumento === 'moderno') {
        const f = await listFichasYiModerno(desde, hasta);
        if (!vigente()) return;
        setFichasMod(f);
        setFichasBod([]); setFichasMer([]); setCierres([]);
      } else if (instrumento === 'bodegas') {
        const f = await listFichasYiBodega(desde, hasta);
        if (!vigente()) return;
        setFichasBod(f);
        setFichasMod([]); setFichasMer([]); setCierres([]);
      } else {
        const [f, c] = await Promise.all([listFichasYiMercado(desde, hasta), listCierresYiMercado(desde, hasta)]);
        if (!vigente()) return;
        setFichasMer(f); setCierres(c);
        setFichasMod([]); setFichasBod([]);
      }
    } catch (e) {
      if (!vigente()) return;
      // El mensaje del servidor va primero y tal cual: reemplazarlo por uno propio ya costó caro.
      setError(e instanceof Error ? e.message : 'No se pudieron cargar las fichas.');
    } finally {
      if (vigente()) setCargando(false);
    }
  }, [instrumento, desde, hasta]);

  useEffect(() => { void cargar(); }, [cargar]);

  // Al cambiar de instrumento, la segunda barra deja de tener sentido: una zona de bodegas no es una
  // tienda. Un filtro que sobrevive al cambio esconde todo sin decir por qué.
  useEffect(() => { setSitio(''); setPersona(''); setBusqueda(''); }, [instrumento]);
  // «Solo donde se encontró» es de UNA marca: con las dos no tiene sentido, y no puede quedar puesto
  // a escondidas recortando filas.
  useEffect(() => { if (marca === 'las_dos') setSoloPresente(false); }, [marca]);

  // ── La segunda barra: recorta lo ya traído ───────────────────────────────

  const sitios = useMemo(() => {
    if (instrumento === 'moderno') return agruparTextos(fichasMod.map((f) => f.tiendaTexto), []);
    if (instrumento === 'bodegas') return agruparTextos(fichasBod.map((f) => f.zona), []);
    return agruparTextos(fichasMer.map((f) => f.mercadoNombre), STOPWORDS_LUGAR);
  }, [instrumento, fichasMod, fichasBod, fichasMer]);

  const personas = useMemo(() => agruparPersonas(
    instrumento === 'moderno' ? fichasMod.map((f) => f.supervisor)
      : instrumento === 'bodegas' ? fichasBod.map((f) => f.supervisor) : fichasMer.map((f) => f.supervisor),
  ), [instrumento, fichasMod, fichasBod, fichasMer]);

  const modBase = useMemo(() => fichasMod.filter((f) => pasaFiltro(
    { sitio, persona, busqueda, soloSenal }, [], f.flags, f.tiendaTexto, f.supervisor, f.tiendaTexto, f.cadena, f.formato,
  )), [fichasMod, sitio, persona, busqueda, soloSenal]);
  const bodBase = useMemo(() => fichasBod.filter((f) => pasaFiltro(
    { sitio, persona, busqueda, soloSenal }, [], f.flags, f.zona, f.supervisor, f.tiendaNombre, f.tiendaDireccion, f.zona, f.distrito,
  )), [fichasBod, sitio, persona, busqueda, soloSenal]);
  const merBase = useMemo(() => fichasMer.filter((f) => pasaFiltro(
    { sitio, persona, busqueda, soloSenal }, STOPWORDS_LUGAR, f.flags, f.mercadoNombre, f.supervisor,
    f.mercadoNombre, f.puestoNombre, f.distrito, f.puestoNumero === null ? null : String(f.puestoNumero),
  )), [fichasMer, sitio, persona, busqueda, soloSenal]);

  // ── «Solo donde se encontró»: el único filtro de marca que SÍ saca filas ──

  const presenciaDe = useMemo((): ((i: number) => Presencia) | null => {
    if (marca === 'las_dos') return null;
    if (instrumento === 'moderno') return (i) => presenciaModerno(modBase[i], marca);
    if (instrumento === 'bodegas') return (i) => presenciaBodega(bodBase[i], marca);
    return (i) => presenciaMercado(merBase[i], marca);
  }, [marca, instrumento, modBase, bodBase, merBase]);

  const base = instrumento === 'moderno' ? modBase.length : instrumento === 'bodegas' ? bodBase.length : merBase.length;
  const cuentaPresencia = useMemo(() => {
    const c: Record<Presencia, number> = { presente: 0, ausente: 0, no_se_sabe: 0 };
    if (presenciaDe !== null) for (let i = 0; i < base; i += 1) c[presenciaDe(i)] += 1;
    return c;
  }, [presenciaDe, base]);

  const recorta = soloPresente && presenciaDe !== null;
  const modVisibles = useMemo(() => (recorta && instrumento === 'moderno' && presenciaDe !== null
    ? modBase.filter((_, i) => presenciaDe(i) === 'presente') : modBase), [recorta, instrumento, presenciaDe, modBase]);
  const bodVisibles = useMemo(() => (recorta && instrumento === 'bodegas' && presenciaDe !== null
    ? bodBase.filter((_, i) => presenciaDe(i) === 'presente') : bodBase), [recorta, instrumento, presenciaDe, bodBase]);
  const merVisibles = useMemo(() => (recorta && instrumento === 'mercados' && presenciaDe !== null
    ? merBase.filter((_, i) => presenciaDe(i) === 'presente') : merBase), [recorta, instrumento, presenciaDe, merBase]);

  const traidas = instrumento === 'moderno' ? fichasMod.length : instrumento === 'bodegas' ? fichasBod.length : fichasMer.length;
  const visibles = instrumento === 'moderno' ? modVisibles.length : instrumento === 'bodegas' ? bodVisibles.length : merVisibles.length;
  const hayFiltro = sitio !== '' || persona !== '' || busqueda.trim() !== '' || soloSenal || recorta;
  // Mientras carga, `traidas` todavía es la lista vieja (o vacía): restarla daría «N fuera del rango» falsos.
  const fueraDelRango = bordes === null || cargando ? 0 : Math.max(0, bordes.total - traidas);
  const estudioVacio = bordes !== null && bordes.total === 0;
  // Hasta que lleguen los bordes no hay rango, así que tampoco hay carga: decir «no hay fichas entre esas
  // fechas» en ese momento sería afirmar algo que todavía nadie consultó. Si los bordes fallaron, el
  // error ya está a la vista y no se sigue diciendo «cargando».
  const esperando = cargando || (bordes === null && error === '');

  const limpiar = () => {
    setSitio(''); setPersona(''); setBusqueda(''); setSoloSenal(false); setSoloPresente(false);
  };

  const rotuloSitio = instrumento === 'moderno' ? 'Tienda' : instrumento === 'bodegas' ? 'Zona' : 'Mercado';
  const nombreMarca = marca === 'las_dos' ? null : NOMBRE_MARCA[marca];

  return (
    <div>
      <p className="mb-4 max-w-3xl text-sm text-gray-600">
        Canal moderno (góndola), bodegas y mercados del estudio de <b>Yichang</b>: la góndola de snacks es de
        Pringles y la de cereales, de Kellogg&apos;s. Una misma visita mide las dos marcas. Cada fila es una ficha de
        campo y se abre para ver todo lo que se levantó, con sus fotos.
        {' '}
        <span className="text-gray-500">No es el levantamiento de P&amp;G ni el de Softys: son otras tablas y otro formulario.</span>
      </p>

      {/* Primera barra: lo que viaja al servidor, y el foco de marca. */}
      <div className="mb-2 flex flex-wrap items-end gap-3">
        <label className="text-sm">
          Instrumento
          <select aria-label="Instrumento" value={instrumento}
            onChange={(e) => setInstrumento(e.target.value as InstrumentoYi)}
            className="mt-1 block rounded border px-2 py-1.5">
            <option value="moderno">Canal moderno (góndola)</option>
            <option value="bodegas">Bodegas (canal tradicional)</option>
            <option value="mercados">Mercados (canal tradicional)</option>
          </select>
        </label>
        <label className="text-sm">
          Marca
          <select aria-label="Marca" value={marca} onChange={(e) => setMarca(e.target.value as MarcaYi)}
            className="mt-1 block rounded border px-2 py-1.5">
            <option value="las_dos">Las dos</option>
            <option value="pringles">Pringles</option>
            <option value="kelloggs">Kellogg&apos;s</option>
          </select>
        </label>
        <label className="text-sm">
          Desde
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
            className="mt-1 block rounded border px-2 py-1.5" />
        </label>
        <label className="text-sm">
          Hasta
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
            className="mt-1 block rounded border px-2 py-1.5" />
        </label>
        <button type="button" onClick={() => void cargar()} disabled={cargando}
          className="rounded border border-gray-300 px-3 py-1.5 text-sm">
          {cargando ? 'Cargando…' : 'Actualizar'}
        </button>
        {bordes !== null && (
          <span className="pb-1.5 text-xs text-gray-500">
            {bordes.primera === null
              ? 'Todavía no hay ninguna ficha de este instrumento.'
              : <>El estudio va del {bordes.primera} al {bordes.ultima}</>}
            {fueraDelRango > 0 && (
              <>
                {' · '}
                <b className="text-amber-700">{fueraDelRango} {fueraDelRango === 1 ? 'ficha' : 'fichas'} fuera del rango</b>
              </>
            )}
          </span>
        )}
      </div>

      {/* Segunda barra: recorta lo ya traído, sin volver al servidor. */}
      <div className="mb-4 flex flex-wrap items-end gap-3 border-t border-gray-100 pt-3">
        <label className="text-sm">
          {rotuloSitio}
          <select aria-label={rotuloSitio} value={sitio} onChange={(e) => setSitio(e.target.value)}
            className="mt-1 block max-w-xs rounded border px-2 py-1.5">
            <option value="">Todos</option>
            {sitios.map((s) => (
              <option key={s.clave} value={s.clave}>
                {s.rotulo} · {s.n}{s.grafias > 1 ? ` (${s.grafias} grafías)` : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          Supervisor
          <select aria-label="Supervisor" value={persona} onChange={(e) => setPersona(e.target.value)}
            className="mt-1 block max-w-xs rounded border px-2 py-1.5">
            <option value="">Todos</option>
            {personas.map((p) => (
              <option key={p.clave} value={p.clave}>
                {p.rotulo} · {p.n}{p.grafias.length > 1 ? ` (${p.grafias.length} grafías)` : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          Buscar
          <input type="search" aria-label="Buscar" value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
            placeholder={instrumento === 'moderno' ? 'Ej.: plazavea surco' : instrumento === 'bodegas' ? 'Ej.: comas rosa' : 'Ej.: unicachi 12'}
            className="mt-1 block w-64 rounded border px-2 py-1.5" />
        </label>
        <label className="flex items-center gap-2 pb-1.5 text-sm">
          <input type="checkbox" checked={soloSenal} onChange={(e) => setSoloSenal(e.target.checked)} />
          Solo con alguna señal
        </label>
        {nombreMarca !== null && (
          <label className="flex items-center gap-2 pb-1.5 text-sm">
            <input type="checkbox" checked={soloPresente} onChange={(e) => setSoloPresente(e.target.checked)} />
            Solo donde se encontró {nombreMarca}
          </label>
        )}
      </div>

      {error !== '' && (
        <p role="alert" className="my-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-900">{error}</p>
      )}

      {/* La línea de reconciliación. Va SIEMPRE: es el único control que se puede hacer de un vistazo,
          y sin ella un resumen filtrado se lee como el resumen de todo. */}
      <p className="mb-3 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
        <b>{visibles}</b> de <b>{traidas}</b> fichas traídas
        {' · '}{NOMBRE_INSTRUMENTO[instrumento]}
        {' · '}{NOMBRE_FOCO[marca]}
        {' · '}{desde} a {hasta}
        {recorta && nombreMarca !== null && (
          <>
            {' · solo donde se encontró '}{nombreMarca}: quedan afuera <b>{cuentaPresencia.ausente}</b> sin {nombreMarca}
            {' y '}<b>{cuentaPresencia.no_se_sabe}</b> donde no se puede saber
          </>
        )}
        {hayFiltro && (
          <>
            {' · con filtro '}
            <button type="button" onClick={limpiar} className="text-blue-700 underline">limpiar</button>
          </>
        )}
        {nombreMarca !== null && !recorta && (
          <span className="block text-xs text-gray-500">
            La marca no saca fichas: una visita mide las dos. Enfoca lo que se muestra de cada una en {nombreMarca}.
          </span>
        )}
      </p>

      {/* Render CONDICIONAL y no `hidden`: cualquier `display` propio le gana al `[hidden]` del navegador. */}
      <div className="mb-4 flex flex-wrap gap-1 border-b border-gray-200">
        {([['registros', 'Registros'], ['resumen', 'Resumen']] as const).map(([k, label]) => (
          <button key={k} type="button" onClick={() => setSolapa(k)}
            aria-current={solapa === k ? 'page' : undefined}
            className={`-mb-px border-b-2 px-4 py-3 text-sm ${solapa === k
              ? 'border-blue-600 font-semibold text-blue-700'
              : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900'}`}>
            {label} · {NOMBRE_INSTRUMENTO[instrumento]}
          </button>
        ))}
      </div>

      {solapa === 'registros' ? (
        <RegistrosYichang
          instrumento={instrumento} moderno={modVisibles} bodegas={bodVisibles} mercados={merVisibles}
          cierres={cierres} marca={marca} cargando={esperando} hayFiltro={hayFiltro} onLimpiar={limpiar}
          estudioVacio={estudioVacio}
        />
      ) : (
        <ResumenYichang
          instrumento={instrumento} moderno={modVisibles} bodegas={bodVisibles} mercados={merVisibles}
          cierres={cierres} marca={marca} estudioVacio={estudioVacio} hayFiltro={hayFiltro} cargando={esperando}
        />
      )}
    </div>
  );
}
